import React, { Fragment, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button, DropdownItem, DropdownMenu,
  DropdownToggle, Media, Row, UncontrolledDropdown, Badge
} from 'reactstrap';
import { MoreVertical, PlusCircle, ToggleLeft, ToggleRight, Trash2, Eye, Edit, Calendar, User } from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { baseURL } from '../../../../Services/api/baseURL';
import Loader from '../../../Loader/Loader';
import { useNavigate } from 'react-router-dom';
import useRbac from '../../../../Hooks/useRbac';

const BlogTable = () => {
  const navigate = useNavigate();
  const userRole = JSON.parse(localStorage.getItem('role_name'));
  const { can, canAny } = useRbac();
  const token = JSON.parse(localStorage.getItem("token"));

  const [searchTerm, setSearchTerm] = useState('');
  const [blogData, setBlogData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRows: 0
  });
  const [loading, setLoading] = useState(false);

  // Helper function to get image URL - handles both string and object formats
  const getImageUrl = (images) => {
    if (!images || images.length === 0) return null;
    const firstImage = images[0];
    // Handle both string URL and object with url property
    if (typeof firstImage === 'string') {
      return firstImage;
    }
    if (typeof firstImage === 'object' && firstImage.url) {
      return firstImage.url;
    }
    return null;
  };

  // Helper function to get category name
  const getCategoryName = (blog) => {
    if (blog.category?.name) return blog.category.name;
    if (blog.categoryName) return blog.categoryName;
    if (typeof blog.category === 'string') return blog.category;
    return null;
  };

  // API Calls
  const fetchBlogs = async (page) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/api/blogs/getAll`, {
        params: {
          page,
          limit: pagination.perPage
        },
        headers: {
          Authorization: `${token}`,
        }
      });
      console.log('Blog fetch response:', response);
      const { blogs, total, totalPages } = response.data;
      setBlogData(blogs);
      setPagination(prev => ({
        ...prev,
        totalRows: total,
        totalPages: totalPages
      }));
    } catch (error) {
      console.error('Error fetching blogs:', error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch blogs",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const endpoint = newStatus === 'active' ?
        `${baseURL}/api/blogs/${id}/activate` :
        `${baseURL}/api/blogs/${id}/deactivate`;

      await axios.patch(
        endpoint,
        { status: newStatus },
        {
          headers: {
            Authorization: `${token}`,
          }
        }
      );

      Swal.fire({
        title: newStatus === 'active' ? "Blog Published!" : "Blog Unpublished!",
        icon: "success",
        confirmButtonColor: "#fc2c54",
      });

      fetchBlogs(pagination.page);
    } catch (error) {
      console.error('Error updating blog status:', error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to update blog status",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#D31789",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        await axios.delete(`${baseURL}/api/blogs/${id}`, {
          headers: {
            Authorization: `${token}`,
          }
        });

        Swal.fire({
          title: "Deleted!",
          text: "Blog has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#fc2c54",
        });

        fetchBlogs(pagination.page);
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to delete blog",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  const handleEditBlog = (row) => {
    navigate(`/blog-edit/${row._id}`);
  };

  const handleViewBlog = (row) => {
    navigate(`/blog-detail/${row._id}`);
  };

  const handleCreateBlog = () => {
    navigate('/blog-create');
  };

  // Table Configuration
  const columns = [
    {
      name: 'Image',
      selector: row => getImageUrl(row.images),
      sortable: false,
      center: true,
      cell: row => {
        const imageUrl = getImageUrl(row.images);
        const altText = row.images?.[0]?.altText || row.title || 'Blog image';
        
        return (
          <div className="blog-image-container" style={{ padding: '4px' }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={altText}
                style={{
                  width: '60px',
                  height: '45px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              style={{
                width: '60px',
                height: '45px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '6px',
                display: imageUrl ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6c757d',
                fontSize: '11px',
                textAlign: 'center',
                border: '1px solid #e9ecef'
              }}
            >
              No Image
            </div>
          </div>
        );
      },
      width: '100px'
    },
    {
      name: 'Blog Details',
      selector: row => row.title,
      sortable: true,
      cell: row => {
        const categoryName = getCategoryName(row);
        
        return (
          <div style={{ padding: '8px 0' }}>
            <div style={{
              fontWeight: '600',
              marginBottom: '4px',
              fontSize: '14px',
              color: '#2c3e50',
              lineHeight: '1.3'
            }}>
              {row.title.length > 50 ? row.title.substring(0, 50) + '...' : row.title}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#6c757d',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.3',
              marginBottom: '4px'
            }}>
              {row.excerpt || row.description || 'No excerpt available'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
              {categoryName && (
                <Badge color="info" style={{ fontSize: '9px', padding: '2px 6px' }}>
                  {categoryName}
                </Badge>
              )}
            </div>
          </div>
        );
      },
      grow: 2
    },
    {
      name: 'Author',
      selector: row => row.createdBy?.name,
      sortable: true,
      center: true,
      cell: row => (
        <div style={{ display: "flex", textAlign: 'center', fontSize: '12px' }} >
          <div style={{ display: "flex", fontWeight: '500', color: '#495057', marginBottom: '2px' }}>
            <User size={10} className="me-1" />
            {row.createdBy?.name || 'Admin'}
          </div>
        </div>
      ),
      width: '120px'
    },
    {
      name: 'Date',
      selector: row => row.createdAt,
      sortable: true,
      center: true,
      cell: row => (
        <div style={{ display: "flex", gap: "5px", textAlign: 'center', fontSize: '11px' }}>
          <div style={{ display: "flex", color: '#6c757d', marginBottom: '2px' }}>
            <Calendar size={10} className="me-1" />
            {moment(row.createdTime || row.createdAt).format("MMM DD, YYYY")}
          </div>
          <div style={{ color: '#6c757d', fontSize: '10px' }}>
            {moment(row.createdTime || row.createdAt).format("hh:mm A")}
          </div>
        </div>
      ),
      width: '250px'
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      center: true,
      cell: row => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'active': return 'success';
            case 'draft': return 'warning';
            case 'inactive': return 'danger';
            default: return 'secondary';
          }
        };

        const getStatusText = (status) => {
          switch (status) {
            case 'active': return 'Published';
            case 'draft': return 'Draft';
            case 'inactive': return 'Unpublished';
            default: return status;
          }
        };

        return (
          <Badge color={getStatusColor(row.status)} style={{ fontSize: '10px', padding: '4px 8px' }}>
            {getStatusText(row.status)}
          </Badge>
        );
      },
      width: '110px'
    },
    {
      name: 'Actions',
      omit: !canAny(['blogs.update', 'blogs.publish', 'blogs.delete']),
      cell: row => (
        <UncontrolledDropdown>
          <DropdownToggle tag="span" className="p-2 cursor-pointer">
            <MoreVertical size={14} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => handleViewBlog(row)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
              <Eye size={12} className="me-2" /> View
            </DropdownItem>
            {can('blogs.update') && <DropdownItem onClick={() => handleEditBlog(row)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
              <Edit size={12} className="me-2" /> Edit
            </DropdownItem>}
            {can('blogs.publish') && (
              <DropdownItem
                onClick={() => handleUpdateStatus(row._id, row.status === 'active' ? 'inactive' : 'active')}
                className={row.status === 'active' ? "text-danger" : "text-success"}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}
              >
                {row.status === 'active' ? (
                  <>
                    <ToggleLeft size={12} className="me-2" /> Unpublish
                  </>
                ) : (
                  <>
                    <ToggleRight size={12} className="me-2" /> Publish
                  </>
                )}
              </DropdownItem>
            )}
            {can('blogs.delete') && <DropdownItem
              onClick={() => handleDelete(row._id)}
              className="text-danger"
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}
            >
              <Trash2 size={12} className="me-2" /> Delete
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      sortable: false,
      center: true,
      width: '80px'
    }
  ];

  // Effects
  useEffect(() => {
    fetchBlogs(1);
  }, []);

  const filteredBlogs = blogData.filter(item => {
    const categoryName = getCategoryName(item) || '';
    return (
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  });

  // Render
  return (
    <Fragment>
      <Row className='pb-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            {/* <H4>Blog Management</H4> */}
             <h5 className="fw-bolder">Blog Management</h5>
            <p className="text-muted mb-0">Manage your blog posts, and content</p>
          </div>
          <div className="file-content">
            <Media>
              <div className='mb-0 form-group position-relative search_outer d-flex align-items-center'>
                <i className='fa fa-search'></i>
                <input
                  className='form-control border-0'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  type='text'
                  placeholder='Search blogs, categories, tags...'
                  style={{ paddingLeft: '35px' }}
                />
              </div>
              {can('blogs.create') && (
                <Media body className='text-end ms-3'>
                  <Button className='btn btn-primary d-flex align-items-center' onClick={handleCreateBlog}>
                    <PlusCircle size={16} className="me-2" />
                    Create New Blog
                  </Button>
                </Media>
              )}
            </Media>
          </div>
        </div>
      </Row>

      <DataTable
        data={filteredBlogs}
        columns={columns}
        pagination
        paginationServer
        paginationTotalRows={pagination.totalRows}
        onChangePage={(page) => {
          setPagination(prev => ({ ...prev, page }));
          fetchBlogs(page);
        }}
        onChangeRowsPerPage={(newPerPage, page) => {
          setPagination(prev => ({ ...prev, perPage: newPerPage, page }));
          fetchBlogs(page);
        }}
        progressPending={loading}
        progressComponent={<Loader />}
        customStyles={{
          rows: {
            style: {
              minHeight: '80px',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
              },
            },
          },
          headCells: {
            style: {
              paddingLeft: '8px',
              paddingRight: '8px',
              fontWeight: '600',
              fontSize: '13px',
              color: '#495057',
            },
          },
          cells: {
            style: {
              paddingLeft: '8px',
              paddingRight: '8px',
            },
          },
        }}
      />
    </Fragment>
  );
};

export default BlogTable;